// Settings → Payments → M-Pesa: one M-Pesa business account PER COMPANY (legal
// entity). Each company pays its employees' wallets from its OWN M-Pesa account —
// KaziPeople never holds the money. A company without a connection cannot pay
// wallets (payments fail closed with a clear reason, never re-routed).
//
// Credentials are write-only: they are encrypted server-side and never shown again.
// "Test connection" only opens a session with M-Pesa (getSession) — it never sends
// a payment. Production (live money) is selectable only by someone who may also
// release payments (the owner), and only on the production service.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ShieldCheck, Smartphone, XCircle } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { listLegalEntities, type LegalEntity } from '@/services/payrollSettingsService';
import { paygateApi, type ConnectionsView, type ProviderConfig, type ProviderEnv } from '../api/paygateApi';

const inputClass =
  'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100';

/** The workspace-level connection (only offered when the workspace has no companies). */
const WORKSPACE = '__workspace__';

type Creds = { api_key: string; public_key: string; service_provider_code: string; payment_items_desc: string };
const EMPTY: Creds = { api_key: '', public_key: '', service_provider_code: '', payment_items_desc: '' };

/** Status of one company's M-Pesa connection in one environment. */
export type ConnectionState = 'not_configured' | 'no_credentials' | 'disabled' | 'active';

export function connectionState(c?: ProviderConfig): ConnectionState {
  if (!c) return 'not_configured';
  if (!c.enabled) return 'disabled';
  if (!c.hasCredentials) return 'no_credentials';
  return 'active';
}

const STATE_BADGE: Record<ConnectionState, { label: string; variant: 'success' | 'warning' | 'secondary' | 'danger' }> = {
  active: { label: 'Connected', variant: 'success' },
  no_credentials: { label: 'Credentials missing', variant: 'warning' },
  disabled: { label: 'Disabled', variant: 'danger' },
  not_configured: { label: 'Not connected', variant: 'secondary' },
};

const envLabel = (e: ProviderEnv) => (e === 'live' ? 'Production' : 'Sandbox');

export const MpesaConnectionsCard: React.FC = () => {
  const [view, setView] = useState<ConnectionsView | null>(null);
  const [entities, setEntities] = useState<LegalEntity[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entity, setEntity] = useState<string>('');
  const [env, setEnv] = useState<ProviderEnv>('sandbox');
  const [creds, setCreds] = useState<Creds>(EMPTY);
  const [busy, setBusy] = useState<'save' | 'test' | 'disable' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [v, ents] = await Promise.all([paygateApi.listConnections(), listLegalEntities()]);
      setView(v);
      setEntities(ents);
      setEntity((cur) => cur || (ents.length > 0 ? ents[0].id : WORKSPACE));
    } catch (e) {
      // Never render "nothing configured" when we simply could not load.
      setLoadError(getApiErrorMessage(e, 'Could not load your M-Pesa connections.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const mpesa = useMemo(() => (view?.connections ?? []).filter((c) => c.providerCode === 'mpesa'), [view]);
  const entityId = entity === WORKSPACE ? undefined : entity;
  const current = useMemo(
    () => mpesa.find((c) => (c.legalEntityId ?? WORKSPACE) === entity && c.environment === env),
    [mpesa, entity, env],
  );
  const state = connectionState(current);
  const canManage = !!view?.canManage;
  const liveSelectable = !!view?.canChooseLive && !!view?.liveAvailable;
  const typed = Object.values(creds).some((v) => v.trim() !== '');
  const needsAll = !current?.hasCredentials;

  // Reset the write-only form whenever the selection changes.
  useEffect(() => { setCreds(EMPTY); setRefusal(null); setConfirmDisable(false); }, [entity, env]);

  const companyName = (id?: string) =>
    id ? entities?.find((e) => e.id === id)?.name ?? 'Unknown company' : 'Whole workspace';

  const save = async () => {
    setRefusal(null);
    if (needsAll && (!creds.api_key.trim() || !creds.public_key.trim() || !creds.service_provider_code.trim())) {
      setRefusal('Enter the API key, public key and business short code to connect M-Pesa.');
      return;
    }
    if (!needsAll && typed && (!creds.api_key.trim() || !creds.public_key.trim() || !creds.service_provider_code.trim())) {
      setRefusal('To replace the credentials, enter all three: API key, public key and business short code.');
      return;
    }
    setBusy('save');
    try {
      await paygateApi.saveProvider('mpesa', {
        legalEntityId: entityId,
        channel: 'MOBILE_MONEY',
        environment: env,
        enabled: true,
        priority: current?.priority ?? 10,
        credentials: typed
          ? {
              api_key: creds.api_key.trim(),
              public_key: creds.public_key.trim(),
              service_provider_code: creds.service_provider_code.trim(),
              market: 'vodacomTZN',
              country: 'TZN',
              currency: 'TZS',
              ...(creds.payment_items_desc.trim() ? { payment_items_desc: creds.payment_items_desc.trim() } : {}),
            }
          : null,
      });
      toast.success(current?.hasCredentials && typed ? 'M-Pesa credentials replaced.' : 'M-Pesa connection saved.');
      setCreds(EMPTY);
      await load();
    } catch (e) {
      setRefusal(getApiErrorMessage(e, 'Could not save the M-Pesa connection.'));
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    if (!current) return;
    setRefusal(null);
    setBusy('test');
    try {
      const r = await paygateApi.testConnection(current.id);
      if (r.ok) toast.success('M-Pesa accepted the credentials. No payment was made.');
      else toast.error(r.detail || 'M-Pesa refused the credentials.');
      await load();
    } catch (e) {
      setRefusal(getApiErrorMessage(e, 'Could not test the connection.'));
    } finally {
      setBusy(null);
    }
  };

  const disable = async () => {
    if (!current) return;
    setBusy('disable');
    try {
      await paygateApi.disableConnection(current.id);
      toast.success('M-Pesa disabled for future payments. Payments already sent are still tracked.');
      setConfirmDisable(false);
      await load();
    } catch (e) {
      setRefusal(getApiErrorMessage(e, 'Could not disable the connection.'));
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <Card>
        <CardContent className="p-5 space-y-3" data-testid="mpesa-load-error">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-charcoal" />
            <h2 className="font-semibold text-foreground">M-Pesa (per company)</h2>
          </div>
          <p className="text-sm text-danger-strong">{loadError}</p>
          <Button size="sm" variant="outline" onClick={() => void load()}>Try again</Button>
        </CardContent>
      </Card>
    );
  }
  if (!view || !entities) return <Skeleton className="h-48 w-full" />;

  const companies: { id: string; name: string }[] =
    entities.length > 0 ? entities.map((e) => ({ id: e.id, name: e.name })) : [{ id: WORKSPACE, name: 'Whole workspace' }];

  return (
    <Card>
      <CardContent className="p-5 space-y-5" data-testid="mpesa-connections">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 h-5 w-5 text-charcoal" />
          <div>
            <h2 className="font-semibold text-foreground">M-Pesa (per company)</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pay employees&rsquo; M-Pesa wallets from each company&rsquo;s own M-Pesa business account. The money moves
              straight from your account to your employees — KaziPeople never holds it. A company without its own
              connection cannot pay wallets: those payments are stopped with a clear reason, never sent another way.
            </p>
          </div>
        </div>

        {/* Overview: every company × environment */}
        <div className="space-y-2" data-testid="mpesa-overview">
          {companies.map((co) => {
            const rows = (['sandbox', 'live'] as ProviderEnv[]).map((e) => ({
              e,
              c: mpesa.find((m) => (m.legalEntityId ?? WORKSPACE) === co.id && m.environment === e),
            }));
            return (
              <div key={co.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                <span className="text-sm font-medium text-foreground">{co.name}</span>
                <span className="flex flex-wrap gap-2">
                  {rows.map(({ e, c }) => {
                    const st = connectionState(c);
                    return (
                      <Badge key={e} variant={STATE_BADGE[st].variant}>
                        {envLabel(e)}: {STATE_BADGE[st].label}
                      </Badge>
                    );
                  })}
                </span>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="mpesa-company" className="mb-1 block text-sm font-medium text-foreground">Company</label>
            <select id="mpesa-company" className={inputClass} value={entity} onChange={(e) => setEntity(e.target.value)}>
              {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="mpesa-env" className="mb-1 block text-sm font-medium text-foreground">Environment</label>
            <select id="mpesa-env" className={inputClass} value={env} onChange={(e) => setEnv(e.target.value as ProviderEnv)}>
              <option value="sandbox">Sandbox (testing — no real money)</option>
              <option value="live" disabled={!liveSelectable}>Production (live money)</option>
            </select>
            {!liveSelectable ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {view.liveAvailable
                  ? 'Only the company owner can set up a production connection.'
                  : 'Production connections can only be set up in the production KaziPeople environment.'}
              </p>
            ) : null}
          </div>
        </div>

        {env === 'live' ? (
          <div className="flex items-start gap-2 rounded-lg border border-warning bg-warning-soft px-3 py-2 text-sm text-warning-strong" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Production moves real money from {companyName(entityId)}&rsquo;s M-Pesa business account to employee wallets.
              Use it only after a successful sandbox test and your M-Pesa account is approved for B2C payments.
            </span>
          </div>
        ) : null}

        {/* Status of the selected connection */}
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm" data-testid="mpesa-status">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{companyName(entityId)} · {envLabel(env)}</span>
            <Badge variant={STATE_BADGE[state].variant}>{STATE_BADGE[state].label}</Badge>
            {current?.hasCredentials ? (
              <span className="text-xs text-muted-foreground">Credentials version {current.credentialVersion}</span>
            ) : null}
          </div>
          {current?.lastTestAt ? (
            <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              {current.lastTestOk ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-danger" />}
              Last test {new Date(current.lastTestAt).toLocaleString()}: {current.lastTestDetail ?? (current.lastTestOk ? 'connected' : 'failed')}
            </p>
          ) : current ? (
            <p className="mt-2 text-xs text-muted-foreground">Not tested yet.</p>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              No M-Pesa connection for this company — wallet payments from it will be stopped until one is connected.
            </p>
          )}
        </div>

        {canManage ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="mpesa-api-key" className="mb-1 block text-sm font-medium text-foreground">API key</label>
                <input id="mpesa-api-key" type="password" autoComplete="new-password" className={inputClass}
                  value={creds.api_key} onChange={(e) => setCreds((c) => ({ ...c, api_key: e.target.value }))}
                  placeholder={current?.hasCredentials ? '•••••• (saved — leave blank to keep)' : 'From the M-Pesa OpenAPI portal'} />
              </div>
              <div>
                <label htmlFor="mpesa-short-code" className="mb-1 block text-sm font-medium text-foreground">Business short code</label>
                <input id="mpesa-short-code" type="text" autoComplete="off" className={inputClass}
                  value={creds.service_provider_code} onChange={(e) => setCreds((c) => ({ ...c, service_provider_code: e.target.value }))}
                  placeholder={current?.hasCredentials ? '•••••• (saved — leave blank to keep)' : 'Service provider code that funds payments'} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="mpesa-public-key" className="mb-1 block text-sm font-medium text-foreground">Public key</label>
                <textarea id="mpesa-public-key" rows={3} className={inputClass}
                  value={creds.public_key} onChange={(e) => setCreds((c) => ({ ...c, public_key: e.target.value }))}
                  placeholder={current?.hasCredentials ? '•••••• (saved — leave blank to keep)' : 'From the M-Pesa OpenAPI portal (base64 or PEM)'} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="mpesa-items-desc" className="mb-1 block text-sm font-medium text-foreground">Payment description (optional)</label>
                <input id="mpesa-items-desc" type="text" autoComplete="off" className={inputClass} maxLength={100}
                  value={creds.payment_items_desc} onChange={(e) => setCreds((c) => ({ ...c, payment_items_desc: e.target.value }))}
                  placeholder="Only if M-Pesa asked you to send one, e.g. Salary" />
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
              Credentials are encrypted and write-only — never shown again. Saving new ones creates a new version;
              payments already sent keep using the version they were sent with.
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              {current && current.enabled ? (
                confirmDisable ? (
                  <span className="flex flex-wrap items-center gap-2" data-testid="mpesa-disable-confirm">
                    <span className="text-xs text-muted-foreground">Stop future M-Pesa payments for {companyName(entityId)}?</span>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDisable(false)} disabled={busy !== null}>Cancel</Button>
                    <Button size="sm" variant="destructive" onClick={() => void disable()} disabled={busy !== null}>
                      {busy === 'disable' ? 'Disabling…' : 'Yes, disable'}
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setConfirmDisable(true)} disabled={busy !== null}>
                    Disable future payments
                  </Button>
                )
              ) : null}
              <Button size="sm" variant="outline" onClick={() => void test()}
                disabled={busy !== null || !current?.hasCredentials || (env === 'live' && !view.liveAvailable)}>
                {busy === 'test' ? 'Testing…' : 'Test connection'}
              </Button>
              <Button size="sm" onClick={() => void save()} disabled={busy !== null || (env === 'live' && !liveSelectable)}>
                {busy === 'save' ? 'Saving…' : current?.hasCredentials ? (typed ? 'Replace credentials' : 'Save & enable') : 'Connect M-Pesa'}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Only users with payment-integration admin access can change M-Pesa connections.</p>
        )}
        <ActionRefusal message={refusal} onDismiss={() => setRefusal(null)} testId="mpesa-refusal" />
      </CardContent>
    </Card>
  );
};

export default MpesaConnectionsCard;
